import React from "react";
import { useLanguage } from "../../i18n/LanguageContext";

const ContactInfo: React.FC = () => {
  const { t, dir } = useLanguage();
  const isRtl = dir === "rtl";

  const content = (t as any)?.contact || {};
  const introTitle =
    content.introTitle || "We're Here to Help\nGet in Touch Anytime";
  const introDescription =
    content.introDescription ||
    "Have a question, need support, or want to speak with our team? Fill out the form below or reach us directly. We respond quickly, and the first consultation is always free.";
  const cardTitle = content.cardTitle || "Contact Eduvate";
  const followUs = content.followUs || "Follow us";
  const officeHoursLabel = content.info?.officeHours || "Office Hours:";
  const officeHoursValue =
    content.info?.officeHoursValue || "Sunday - Thursday, 8:00 AM - 4:00 PM";
  const phoneValue =
    content.info?.phoneValue || "01240079406 / 01240079407";
  const emailValue = content.info?.emailValue || "info@cvis.com.eg";
  const addressValue =
    content.info?.addressValue || "Building No. 129, Tahrir Street, Dokki - Giza";

  const socials = [
    {
      href: "https://facebook.com",
      label: "Facebook",
      icon: (
        <path
          d="M11.1667 17.5V10.8333H13.4167L13.75 8.33333H11.1667V6.75C11.1667 6.02667 11.3667 5.53333 12.4083 5.53333H13.8333V3.3C13.1417 3.225 12.445 3.18889 11.75 3.19167C9.69167 3.19167 8.33333 4.45 8.33333 6.75833V8.33333H6.08333V10.8333H8.33333V17.5H11.1667Z"
          fill="currentColor"
        />
      ),
    },
    {
      href: "https://instagram.com",
      label: "Instagram",
      icon: (
        <>
          <rect
            x="3"
            y="3"
            width="14"
            height="14"
            rx="4"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <circle cx="10" cy="10" r="3.25" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="14.25" cy="5.75" r="0.9" fill="currentColor" />
        </>
      ),
    },
    {
      href: "https://linkedin.com",
      label: "LinkedIn",
      icon: (
        <>
          <path d="M5.16667 7.33333H2.66667V17.3333H5.16667V7.33333Z" fill="currentColor" />
          <path d="M3.91667 5.83333C4.83714 5.83333 5.58333 5.08714 5.58333 4.16667C5.58333 3.24619 4.83714 2.5 3.91667 2.5C2.99619 2.5 2.25 3.24619 2.25 4.16667C2.25 5.08714 2.99619 5.83333 3.91667 5.83333Z" fill="currentColor" />
          <path d="M17.75 11.1083C17.75 8.06667 15.8167 6.91667 13.975 6.91667C12.4875 6.91667 11.825 7.73333 11.45 8.30833V7.33333H8.95C8.98333 7.975 8.95 17.3333 8.95 17.3333H11.45V11.75C11.45 11.45 11.475 11.15 11.5583 10.9333C11.7833 10.3333 12.2917 9.70833 13.15 9.70833C14.275 9.70833 14.725 10.5667 14.725 11.825V17.3333H17.225V11.1083H17.75Z" fill="currentColor" />
        </>
      ),
    },
  ];

  const infoItems = [
    {
      title: content.info?.address || "Location",
      value: addressValue,
      icon: (
        <>
          <path
            d="M12 21C15.5 16.8 18 13.6 18 10C18 6.68629 15.3137 4 12 4C8.68629 4 6 6.68629 6 10C6 13.6 8.5 16.8 12 21Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        </>
      ),
    },
    {
      title: content.info?.phone || "Phone",
      value: phoneValue,
      icon: (
        <path
          d="M7 4H10L11.5 8L9.5 9.5C10.3573 11.2387 11.7613 12.6427 13.5 13.5L15 11.5L19 13V16C19 16.5304 18.7893 17.0391 18.4142 17.4142C18.0391 17.7893 17.5304 18 17 18C13.2868 17.7741 9.79075 16.1971 7.17157 13.5784C4.55238 10.9592 2.97593 7.46317 2.75 3.75C2.75 3.21957 2.96071 2.71086 3.33579 2.33579C3.71086 1.96071 4.21957 1.75 4.75 1.75H8L7 4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: content.info?.email || "Email",
      value: emailValue,
      icon: (
        <>
          <path d="M4 6H20V18H4V6Z" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M4 7L12 13L20 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ),
    },
  ];

  return (
    <section className="flex min-h-screen items-start bg-white px-4 pt-32 pb-16 sm:px-8 sm:pt-36 sm:pb-20 lg:px-16 lg:pt-40 lg:pb-24">
      <div className="w-full pb-16">
        <div
          className={`grid gap-8 lg:grid-cols-2 lg:gap-20 ${
            isRtl ? "lg:[direction:ltr]" : ""
          }`}
        >
          <div
            className={`flex items-end ${
              isRtl ? "lg:order-2" : ""
            }`}
          >
            <div
              className={`flex w-full max-w-[32rem] flex-col gap-4 ${
                isRtl ? "items-end text-right" : "items-start text-left"
              }`}
            >
              <h2 className="whitespace-pre-line font-sans text-2xl font-semibold text-teal-900 sm:text-[32px] sm:leading-[1.2]">
                {introTitle}
              </h2>
              <p className="font-sans text-sm font-normal leading-6 text-black sm:text-base">
                {introDescription}
              </p>
            </div>
          </div>

          <div
            className={`rounded-lg border border-gray-300 bg-white p-6 ${
              isRtl ? "lg:order-1 text-right" : "text-left"
            }`}
          >
            <div
              className={`flex flex-col gap-6 ${
                isRtl ? "items-end" : "items-start"
              }`}
            >
              <h3 className="font-sans text-lg font-semibold leading-7 text-neutral-900">
                {cardTitle}
              </h3>

              {infoItems.map((item) => (
                <div
                  key={item.title}
                  className={`flex w-full items-start gap-3 ${
                    isRtl ? "flex-row-reverse" : ""
                  }`}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center text-second-color">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </svg>
                  </div>
                  <div
                    className={`flex flex-1 flex-col gap-1 ${
                      isRtl ? "items-end text-right" : "items-start text-left"
                    }`}
                  >
                    <div className="font-sans text-base font-semibold leading-6 text-second-color">
                      {item.title}
                    </div>
                    <div className="whitespace-pre-line font-sans text-base font-normal leading-6 text-black">
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}

              <div
                className={`flex w-full flex-col gap-1 ${
                  isRtl ? "items-end text-right" : "items-start text-left"
                }`}
              >
                <div className="font-sans text-base font-bold leading-6 text-second-color">
                  {officeHoursLabel}
                </div>
                <div className="whitespace-pre-line font-sans text-base font-normal leading-6 text-black">
                  {officeHoursValue}
                </div>
              </div>

              <div
                className={`flex flex-col gap-2 ${
                  isRtl ? "items-end text-right" : "items-start text-left"
                }`}
              >
                <div className="font-sans text-lg font-bold leading-7 text-neutral-900">
                  {followUs}
                </div>
                <div className={`flex items-center gap-2 ${isRtl ? "flex-row-reverse" : ""}`}>
                  {socials.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={social.label}
                      className="flex h-8 w-8 items-center justify-center rounded text-neutral-900 transition-colors hover:text-second-color"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        {social.icon}
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;
